// Utility functions for SendNoteModal

// Markdown to HTML converter (kept for backward compatibility)
export const markdownToHtml = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
    .replace(/^- (.*)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n/g, '<br>');
};

// Helper function to strip HTML tags and create plain text version
export const htmlToText = (html: string): string => {
  if (!html) return '';
  
  // Create a temporary div element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  
  // Get text content and preserve line breaks
  let text = tmp.textContent || tmp.innerText || '';
  
  // Replace multiple spaces/newlines with single space, but preserve intentional line breaks
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
};

// Helper function to validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper function to normalize phone number (remove spaces, special chars except +)
export const normalizePhoneNumber = (phone: string): string => {
  return phone.replace(/[^\d+]/g, '');
};

// Helper function to mask phone number for display
export const maskPhoneNumber = (phone: string): string => {
  if (!phone || phone.length < 5) return phone;

  const normalized = normalizePhoneNumber(phone);
  if (normalized.length < 5) return phone;

  // Show first part and mask last 5 digits
  const visiblePart = normalized.slice(0, -5);
  const maskedPart = '*****';
  return `${visiblePart}${maskedPart}`;
};

// Helper function to mask phone number in formatted string (keep name, mask phone)
export const maskPhoneInFormatted = (phone: string): string => {
  if (!phone) return phone;

  // If phone contains ":", separate name and phone
  if (phone.includes(':')) {
    const parts = phone.split(':');
    const namePart = parts[0].trim();
    const phonePart = parts[1]?.trim() || '';

    if (phonePart) {
      const maskedPhone = maskPhoneNumber(phonePart);
      return `${namePart}: ${maskedPhone}`;
    }
  }

  // If no colon, just mask the phone number
  return maskPhoneNumber(phone);
};

// Helper function to extract and deduplicate email addresses from formatted email list
export const extractEmailAddresses = (emailList: string[]): string[] => {
  const emails = emailList
    .filter((email) => email.trim() !== '')
    .map((email) => {
      // If email contains ":", extract the part after the colon
      if (email.includes(':')) {
        return email.split(':').pop()?.trim() || '';
      }
      return email.trim();
    })
    .filter((email) => email !== '' && isValidEmail(email));

  // Remove duplicates using Set (case-sensitive comparison)
  return Array.from(new Set(emails));
};

// Helper function to extract and deduplicate phone numbers from formatted phone list
export const extractPhoneNumbers = (phoneList: string[]): string[] => {
  const phones = phoneList
    .filter((phone) => phone.trim() !== '')
    .map((phone) => {
      // If phone contains ":", extract the part after the colon
      if (phone.includes(':')) {
        return phone.split(':').pop()?.trim() || '';
      }
      return phone.trim();
    })
    .filter((phone) => phone !== '')
    .map((phone) => normalizePhoneNumber(phone)) // Normalize phone format
    .filter((phone) => phone.length >= 10); // Basic phone length validation

  // Remove duplicates using Set
  return Array.from(new Set(phones));
};

// Helper function to get display name from email list
export const getEmailDisplayName = (email: string, emailList: string[]): string => {
  const normalizedEmail = email.toLowerCase().trim();
  const found = emailList.find((item) => {
    const itemEmail = item.includes(':') ? item.split(':').pop()?.trim() : item.trim();
    return itemEmail?.toLowerCase().trim() === normalizedEmail;
  });
  if (found && found.includes(':')) {
    return found.split(':')[0].trim();
  }
  return email;
};

// Helper function to get display name from phone list
export const getPhoneDisplayName = (phone: string, phoneList: string[]): string => {
  const normalizedPhone = normalizePhoneNumber(phone);
  const found = phoneList.find((item) => {
    const itemPhone = item.includes(':') ? item.split(':').pop()?.trim() : item.trim();
    return normalizePhoneNumber(itemPhone || '') === normalizedPhone;
  });
  if (found && found.includes(':')) {
    return found.split(':')[0].trim();
  }
  return phone;
};

